
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface UserProgress {
  hasCompletedAssessment: boolean;
  documentCount: number;
  hasUsedChat: boolean;
  lastLoginDate: string | null;
  isNewUser: boolean;
}

export function useUserProgress() {
  const [progress, setProgress] = useState<UserProgress>({
    hasCompletedAssessment: false,
    documentCount: 0,
    hasUsedChat: false,
    lastLoginDate: null,
    isNewUser: true
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchUserProgress();
  }, []);

  const fetchUserProgress = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const userId = session.user.id;
      
      // Check assessment completion
      const { data: assessment } = await supabase
        .from('business_assessments')
        .select('id')
        .eq('user_id', userId)
        .single();

      // Count documents
      const { data: documents } = await supabase
        .from('documents')
        .select('id')
        .eq('user_id', userId);

      // Check chat usage
      const { data: chatSessions } = await supabase
        .from('chat_sessions')
        .select('id')
        .eq('user_id', userId)
        .limit(1);

      // Check user creation date
      const userCreatedAt = new Date(session.user.created_at);
      const daysSinceCreation = (Date.now() - userCreatedAt.getTime()) / (1000 * 60 * 60 * 24);

      setProgress({
        hasCompletedAssessment: !!assessment,
        documentCount: documents?.length || 0,
        hasUsedChat: (chatSessions?.length || 0) > 0,
        lastLoginDate: session.user.last_sign_in_at,
        isNewUser: daysSinceCreation < 7
      });
    } catch (error) {
      console.error('Error fetching user progress:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getProgressSteps = () => {
    return [
      {
        id: 'assessment',
        title: 'Complete Business Assessment',
        description: 'Tell us about your business for personalized experience',
        completed: progress.hasCompletedAssessment,
        current: !progress.hasCompletedAssessment
      },
      {
        id: 'first-document',
        title: 'Create Your First Document',
        description: 'Start with a Business Plan or Strategic Plan',
        completed: progress.documentCount > 0,
        current: progress.hasCompletedAssessment && progress.documentCount === 0
      },
      {
        id: 'chat',
        title: 'Chat with CSO Agent',
        description: 'Get strategic advice and brainstorm ideas',
        completed: progress.hasUsedChat,
        current: progress.documentCount > 0 && !progress.hasUsedChat
      },
      {
        id: 'multiple-documents',
        title: 'Build Your Strategic Foundation',
        description: 'Create 3+ documents for comprehensive planning',
        completed: progress.documentCount >= 3,
        current: progress.hasUsedChat && progress.documentCount < 3
      }
    ];
  };

  return {
    progress,
    isLoading,
    getProgressSteps,
    refreshProgress: fetchUserProgress
  };
}
